import React from 'react'
import { connect } from 'react-redux'
import { difference, groupBy, uniq, uniqBy } from 'lodash-es'
import { createStructuredSelector } from 'reselect'
import { Locale, Translation } from 'locale'
import {
  CollateralBasketType,
  CollectionItem,
  CollectionType,
  OrderActions,
  OrderBulkCloneArgs,
  OrderBulkSubmitArgs,
  OrderDetail,
  OrderEvent,
  OrderStatus,
  RfqType
} from 'api'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import { ActionItem, SplitButtonDropdown, SplitButtonType } from 'view/components/menus'
import { AssignToCollectionDialog } from 'view/shared/collections/AssignToCollectionDialog'
import { ActionsButtonProps } from 'view/components/datagrid'
import { IndicativeContainer } from 'view/shared/datagrid'
import { SelectionActions } from 'store/selection'
import { createPreparedCollectionIdSelector, preparedStagingOrderIdsSelector } from 'selectors/selectionSelectors'
import {
  createOrderDetailGetterSelector,
  createOrderIdsWithCollectionIdGetterSelector,
  createStagingOrderDetailsSelector,
  orderIdsWithContainerCollectionIdGetterSelector
} from 'selectors/orderSelectors'
import { ReadOnlyCollateralBasketDetailsButton } from 'view/shared/collateralBasket/ReadOnlyCollateralBasketDetailsButton'
import { getMaxCollectionSize, isAonOrOcoCollection, MAX_BULK_STAGING_ACTION_SIZE } from 'model'
import { iconSet } from 'view/components/icons'
import { Decimal } from 'utils'
import { Container as IconContainer } from 'view/components/datagrid/renderers/GroupCellRenderer'
import { CollectionColumnId } from 'view/routes/ColumnNames'

export interface StagingActionsButtonProps extends ModalConsumerProps, ActionsButtonProps<OrderDetail> {
  preparedOrderIds: string[]
  preparedCollectionId: string | null
  getOrderIdsWithCollectionId: (collectionId: string) => string[]
  getOrderIdsWithContainerCollectionId: (containerCollectionId: string) => string[]
  getOrderDetail: (orderId: string) => OrderDetail | null
  onPrepare: (orderDetails: OrderDetail[]) => unknown
  onUnprepare: (orderIds: string[]) => unknown
  onBulkSend: (args: OrderBulkSubmitArgs) => unknown
  onDelete: (orderId: string) => unknown
  onBulkClone: (args: OrderBulkCloneArgs) => unknown
  onRemoveFromCollection: (orderIds: string[]) => unknown
  allStagingOrderDetails: OrderDetail[]
}

const mapStateToProps = createStructuredSelector({
  preparedOrderIds: preparedStagingOrderIdsSelector,
  preparedCollectionId: createPreparedCollectionIdSelector(),
  getOrderIdsWithCollectionId: createOrderIdsWithCollectionIdGetterSelector,
  getOrderIdsWithContainerCollectionId: orderIdsWithContainerCollectionIdGetterSelector,
  getOrderDetail: createOrderDetailGetterSelector,
  allStagingOrderDetails: createStagingOrderDetailsSelector()
})

const mapDispatchToProps = {
  onPrepare: SelectionActions.prepareStagingOrders,
  onUnprepare: SelectionActions.unprepareStagingOrders,
  onBulkSend: OrderActions.bulkSubmit,
  onDelete: OrderActions.delete,
  onBulkClone: OrderActions.bulkClone,
  onRemoveFromCollection: OrderActions.removeFromCollection
}

export class StagingActionsButtonComponent extends React.PureComponent<StagingActionsButtonProps> {
  render() {
    const { allStagingOrderDetails, items, isGroupRow, dataTestId, gridApi } = this.props
    const actions = this.getActions()
    const doOrdersHaveBaskets = allStagingOrderDetails.some(orderDetail => orderDetail.collateralBasket)
    const isIndicative = items.every(orderDetail => orderDetail.order.rfqType === RfqType.INDICATIVE)
    const rowGroupColumns = gridApi?.getRowGroupColumns() ?? []
    const hasCollection = !rowGroupColumns.some(col => col?.getColId() === CollectionColumnId.COLLECTION_NAME)
    return (
      <>
        <SplitButtonDropdown
          dataTestId={dataTestId}
          actions={actions}
          maxButtons={1}
          showDisabledActions={isGroupRow}
          noActionsTooltip={Locale.translate(Translation.SC_MAX_ORDERS_ACTIONS_TOOLTIP, { maxOrders: MAX_BULK_STAGING_ACTION_SIZE })}
          withPortal
        />
        {doOrdersHaveBaskets && !isGroupRow && (
          <ReadOnlyCollateralBasketDetailsButton
            collateralBasketId={items[0].collateralBasket?.id}
            data-test-id="StagingActionsButton-ReadOnlyCollateralBasketDetailsButton"
          />
        )}
        {!isGroupRow && hasCollection && isAonOrOcoCollection(items[0]?.collection?.type) && (
          <IconContainer data-test-id="LiveActionsCell-Collection-Icon" nestingLevel={0} isGroup={false}>
            {iconSet.collectionIcon}
          </IconContainer>
        )}
        {isIndicative && !isGroupRow && <IndicativeContainer>{iconSet.indicativeIcon}</IndicativeContainer>}
      </>
    )
  }

  private getActions = (): ActionItem[] => {
    return [
      {
        label: this.getLabel(Translation.TC_ACTION_SEND),
        type: SplitButtonType.Good,
        onClick: this.handleSend,
        enabled: this.canSend(),
        warnings: this.getWarnings()
      },
      {
        label: this.getLabel(Translation.TC_ACTION_PREPARE),
        type: SplitButtonType.Warn,
        onClick: this.handlePrepare,
        enabled: this.canPrepare(),
        warnings: this.getWarnings()
      },
      {
        label: Locale.translate(Translation.TC_ACTION_STOP_PREPARE),
        type: SplitButtonType.BadLight,
        onClick: this.handleUnprepare,
        enabled: this.canUnprepare(),
        warnings: this.getWarnings()
      },
      {
        label: this.getLabel(Translation.TC_ACTION_DELETE),
        type: SplitButtonType.Bad,
        onClick: this.handleDelete,
        enabled: this.canDelete(),
        warnings: this.getWarnings()
      },
      {
        label: this.getLabel(Translation.TC_ACTION_CLONE),
        onClick: this.handleClone,
        enabled: this.canClone(),
        warnings: this.getWarnings()
      },
      ...(this.props.items.every(i => i.collection?.type !== CollectionType.SMART_GROUPING && !i.containerCollection)
        ? [
            {
              label: this.getLabel(Translation.TC_ACTION_ASSIGN_COLLECTION),
              onClick: this.handleAssignToCollection,
              enabled: this.canAssignToCollection()
            }
          ]
        : []),
      {
        label: this.getLabel(Translation.TC_ACTION_REMOVE_COLLECTION),
        onClick: this.handleRemoveFromCollection,
        enabled: this.canRemoveFromCollection(),
        warnings: this.getWarnings()
      }
    ]
  }

  private getLabel = (baseLabel: Translation) => {
    const { items, isGroupRow } = this.props
    const count = isGroupRow ? ` (${items.length})` : ``
    return `${Locale.translate(baseLabel)}${count}`
  }

  private getWarnings = () => {
    return this.props.items.length > MAX_BULK_STAGING_ACTION_SIZE
      ? [Locale.translate(Translation.SC_MAX_ORDERS_ACTIONS_TOOLTIP, { maxOrders: MAX_BULK_STAGING_ACTION_SIZE })]
      : []
  }

  private handlePrepare = () => {
    const { onPrepare, items } = this.props
    onPrepare(items)
  }

  private handleSend = async () => {
    const { items: orderDetails, onBulkSend, showDialog } = this.props

    // If AON or OCO, send the collection id rather than the order id
    const [aonOcoCollectionIds, orderIds] = orderDetails.reduce<[string[], string[]]>(
      (acc, orderDetail) => {
        const collectionId = orderDetail.order.collectionId

        if (collectionId && !acc[0].includes(collectionId) && isAonOrOcoCollection(orderDetail.collection?.type)) {
          acc[0].push(collectionId)
        } else if (!collectionId || !isAonOrOcoCollection(orderDetail.collection?.type)) {
          acc[1].push(orderDetail.order.orderId)
        }

        return acc
      },
      [[], []]
    )

    const orderDetailsGroupedByCollectionId = groupBy(
      orderDetails.filter(orderDetail => orderDetail.collection),
      orderDetail => orderDetail.collection?.id
    )

    const startConsiderationOrNotionalIsDifferent = (_orderDetails: OrderDetail[]): boolean =>
      _orderDetails.length > 1
        ? (_orderDetails[0].order.notional === null &&
            _orderDetails[0].order.startConsideration !== _orderDetails[1].order.startConsideration) ||
          (_orderDetails[0].order.startConsideration === null && _orderDetails[0].order.notional !== _orderDetails[1].order.notional) ||
          _orderDetails[0].order.notional !== _orderDetails[1].order.notional ||
          _orderDetails[0].order.startConsideration !== _orderDetails[1].order.startConsideration
        : false

    const hasStartCashCurves = Object.values(orderDetailsGroupedByCollectionId).some(
      groupedOrderDetails =>
        groupedOrderDetails.some(orderDetail => orderDetail.collection?.isCurve) &&
        startConsiderationOrNotionalIsDifferent(groupedOrderDetails)
    )

    const hasCurve = (collectionId: string) =>
      orderDetails.filter(orderDetail => orderDetail.collection?.isCurve && orderDetail.collection.id === collectionId).length >= 1

    const setCollectionCurves = (forceCurve: boolean) => {
      const collectionIds: CollectionItem[] = aonOcoCollectionIds.reduce((prev, cur) => {
        if (hasCurve(cur) && startConsiderationOrNotionalIsDifferent(orderDetailsGroupedByCollectionId[cur])) {
          return [
            {
              collectionId: cur,
              forceIsBulk: forceCurve
            },
            ...prev
          ]
        } else if (hasCurve(cur)) {
          return [
            {
              collectionId: cur,
              forceIsBulk: true
            },
            ...prev
          ]
        } else {
          return [
            {
              collectionId: cur,
              forceIsBulk: false
            },
            ...prev
          ]
        }
      }, [])
      return collectionIds
    }

    if (hasStartCashCurves) {
      const dialogArgs = {
        type: NotificationType.Confirm,
        title: Translation.TC_TREAT_AS_CURVE,
        message: Translation.SC_TREAT_AS_CURVE_CONFIRMATION,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        cancelButton: Translation.TC_BUTTON_NO,
        defaultButton: Translation.TC_BUTTON_YES
      }
      const dialogResult = await showDialog(dialogArgs)

      onBulkSend({
        collectionItems: setCollectionCurves(dialogResult === Translation.TC_BUTTON_YES),
        orderIds: orderIds
      })
    } else {
      onBulkSend({
        collectionItems: setCollectionCurves(false),
        orderIds: orderIds
      })
    }
  }

  private handleUnprepare = () => {
    const { onUnprepare, items } = this.props
    onUnprepare(items.map(orderDetail => orderDetail.order.orderId))
  }

  private handleDelete = () => {
    const { onUnprepare, onDelete, items } = this.props

    const orderIdsToUnprepare = items
      .filter(orderDetail => orderDetail.order.status === OrderStatus.PREPARED)
      .map(orderDetail => orderDetail.order.orderId)
    onUnprepare(orderIdsToUnprepare)

    items.forEach(orderDetail => {
      onDelete(orderDetail.order.orderId)
    })
  }

  private handleClone = () => {
    const { onBulkClone, items } = this.props

    const orderIds = items.map(orderDetail => orderDetail.order.orderId)

    onBulkClone({ orderIds })
  }

  private handleAssignToCollection = () => {
    const { items } = this.props
    const orderIds = items.map(orderDetail => orderDetail.order.orderId)
    this.props.showModal(<AssignToCollectionDialog orderIds={orderIds} />)
  }

  private handleRemoveFromCollection = () => {
    const { items } = this.props
    const orderIds = items.map(orderDetail => orderDetail.order.orderId)
    this.props.onRemoveFromCollection(orderIds)
  }

  // All items have the same basket type
  private areBasketTypesValid = (existingBasketType: CollateralBasketType) => {
    const { items } = this.props
    return items.every(item => item.productSector?.basketType === existingBasketType)
  }

  private canPerformEvent = (orderEvent: OrderEvent) => {
    return this.props.items.every(orderDetail => orderDetail.availableEvents.includes(orderEvent))
  }

  private canPrepare = () => {
    const { items, preparedOrderIds, preparedCollectionId, getOrderDetail } = this.props

    if (items.length > MAX_BULK_STAGING_ACTION_SIZE) {
      return false
    }

    const orderIds = items.map(item => item.order.orderId)

    //  All orders valid
    if (items.some(item => item.order.status === OrderStatus.INCOMPLETE)) {
      return false
    }

    // At least one order not already prepared
    if (difference(orderIds, preparedOrderIds).length === 0) {
      return false
    }

    // Selected orders are either all baskets or all collaterals
    if (items.some(item => !!item.collateralBasket !== !!items[0].collateralBasket)) {
      return false
    }

    // Selected items have valid basket types
    if (items[0].productSector?.basketType && !this.areBasketTypesValid(items[0].productSector.basketType)) {
      return false
    }

    if (!this.areEntireAonOcoCollectionsSelected()) {
      return false
    }

    // If a collection is being prepared, don't allow prepare of any other collections or
    // orders unless they are in the same collection and it is of type send_individually
    if (
      preparedCollectionId &&
      !items.every(item => item.collection?.type === CollectionType.SEND_INDIVIDUALLY && item.order.collectionId === preparedCollectionId)
    ) {
      return false
    }

    // If single orders are being prepared, don't allow prepare of collections
    if (!preparedCollectionId && preparedOrderIds.length > 0 && items.some(item => item.order.collectionId)) {
      return false
    }

    // If a basket is being prepared, don't allow prepare of collateral orders or basket orders with incompatible type
    const firstPreparedOrderDetail = preparedOrderIds.length > 0 ? getOrderDetail(preparedOrderIds[0]) : null
    if (firstPreparedOrderDetail?.productSector?.basketType) {
      return this.areBasketTypesValid(firstPreparedOrderDetail.productSector?.basketType)
    }

    // If an order is being prepared, don't allow prepare of any baskets
    return preparedOrderIds.length > 0 ? items.every(item => !item.collateralBasket) : true
  }

  private canUnprepare = () => {
    const { items, preparedOrderIds } = this.props
    return items.some(item => preparedOrderIds.includes(item.order.orderId)) && this.areEntireAonOcoCollectionsSelected()
  }

  private canSend = () => {
    const { items, preparedOrderIds } = this.props

    return (
      items.length > 0 &&
      items.length <= MAX_BULK_STAGING_ACTION_SIZE &&
      this.canPerformEvent(OrderEvent.SUBMIT) &&
      items.every(item => item.order.status === OrderStatus.PREPARED && !preparedOrderIds.includes(item.order.orderId)) &&
      this.areEntireAonOcoCollectionsSelected(true, true)
    )
  }

  private orderDetailsHaveTheSameCounterparties = (orderDetails: OrderDetail[]) =>
    !orderDetails
      .map(({ order }) =>
        order.negotiationInstruction.counterpartyInstructions
          .map(i => i.counterpartyId)
          .sort()
          .join('')
      )
      .some((k, _, a) => k !== a[0])

  private canAssignToCollection = () => {
    const { items, preparedOrderIds } = this.props
    return (
      items.length > 0 &&
      items.length <= MAX_BULK_STAGING_ACTION_SIZE &&
      this.canPerformEvent(OrderEvent.MODIFY_COLLECTION) &&
      items.every(item => !preparedOrderIds.includes(item.order.orderId)) &&
      this.orderDetailsHaveTheSameCounterparties(items)
    )
  }

  private canRemoveFromCollection = () => {
    const { items, preparedOrderIds } = this.props

    return (
      items.length <= MAX_BULK_STAGING_ACTION_SIZE &&
      this.canPerformEvent(OrderEvent.MODIFY_COLLECTION) &&
      items.some(orderDetail => orderDetail.order.collectionId !== null) &&
      items.every(item => !preparedOrderIds.includes(item.order.orderId))
    )
  }

  private canDelete = () => {
    const { items, preparedOrderIds } = this.props

    return (
      items.length > 0 &&
      items.length <= MAX_BULK_STAGING_ACTION_SIZE &&
      this.canPerformEvent(OrderEvent.DELETE) &&
      items.every(item => !preparedOrderIds.includes(item.order.orderId))
    )
  }

  private canClone = () => {
    const { items, getOrderIdsWithCollectionId, getOrderDetail } = this.props

    const itemsByCollectionId = groupBy(items, item => item.order.collectionId)
    const overCollectionSize = Object.entries(itemsByCollectionId)
      .filter(([collectionId, _]) => collectionId !== 'null')
      .map(([collectionId, orderDetails]) => {
        const orderIdsWithCollectionId = getOrderIdsWithCollectionId(collectionId)
        const collectionType = getOrderDetail(orderIdsWithCollectionId[0])?.collection?.type
        return { size: Decimal.add(orderIdsWithCollectionId.length, orderDetails.length), collectionType }
      })
      .some(({ size, collectionType }) => {
        const maxSize = getMaxCollectionSize(collectionType)
        return size > maxSize
      })

    return items.length > 0 && items.length <= MAX_BULK_STAGING_ACTION_SIZE && !overCollectionSize && this.canPerformEvent(OrderEvent.CLONE)
  }

  private areEntireAonOcoCollectionsSelected = (allowMultipleCollections?: boolean, allowSingleOrders?: boolean) => {
    const { items, getOrderIdsWithCollectionId, getOrderIdsWithContainerCollectionId } = this.props

    // only allow AONX prepare panel for complete AONX selection
    const maybeContainerId = items.find(i => !!i.containerCollection)?.containerCollection?.id
    if (maybeContainerId && items.every(i => i?.containerCollection?.id === maybeContainerId)) {
      return items.length === getOrderIdsWithContainerCollectionId(maybeContainerId).length
    }

    // Do not allow multiple collections when allowMultipleCollections is false
    if (!allowMultipleCollections && uniq(items.map(orderDetail => orderDetail.order.collectionId)).length > 1) {
      return false
    }

    // One or more of the selected items is in an AON or OCO collection
    const ordersWithAonOcoCollection = items.filter(item => item.collection && isAonOrOcoCollection(item.collection.type))
    if (ordersWithAonOcoCollection.length === 0) {
      return true
    }

    // Do not allow any single orders or standard collection orders when allowSingleOrders is false
    if (!allowSingleOrders && items.some(item => !item.collection || !isAonOrOcoCollection(item.collection.type))) {
      return false
    }

    const firstOrderInEachAonOcoCollection = uniqBy(ordersWithAonOcoCollection, 'order.collectionId')
    // Check for each selected AON/OCO collection whether all orders in the collection have been selected
    return firstOrderInEachAonOcoCollection.every(firstOrderDetail => {
      const orderIdsWithCollectionId = ordersWithAonOcoCollection

        .filter(orderDetail => orderDetail.order.collectionId === firstOrderDetail.order.collectionId)
        .map(orderDetail => orderDetail.order.orderId)
      if (difference(getOrderIdsWithCollectionId(firstOrderDetail.order.collectionId!), orderIdsWithCollectionId).length !== 0) {
        return false
      }
      return true
    })
  }
}

export const StagingActionsButton = connect(mapStateToProps, mapDispatchToProps)(withModal(StagingActionsButtonComponent))
